import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MessageCircle, Send, Save } from "lucide-react";

const TEMPLATE_LABELS = {
  health_check: "Health check",
  data_issue: "Data issue",
  renewal: "Renewal touch",
  win_back: "Win-back",
  drift_alert: "Drift alert",
};

export default function NudgePicker({ tenantId, tenantName, open, onOpenChange }) {
  const [templates, setTemplates] = useState([]);
  const [chosen, setChosen] = useState("health_check");
  const [mode, setMode] = useState("template"); // "template" | "custom"
  const [custom, setCustom] = useState("");
  const [sending, setSending] = useState(false);
  // Save-as-template state
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const loadTemplates = () => {
    api.get("/platform/nudge/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  };

  useEffect(() => {
    if (!open) return;
    loadTemplates();
    // Reset each open so the last custom draft doesn't leak across tenants
    setMode("template");
    setCustom("");
    setChosen("health_check");
    setSaveOpen(false);
    setSaveLabel("");
  }, [open]);

  const firstName = (tenantName || "there").split(" ")[0];

  const send = async () => {
    setSending(true);
    try {
      let payload;
      if (mode === "custom") {
        if (!custom.trim()) {
          toast.error("Type a message before sending");
          setSending(false);
          return;
        }
        payload = { message: custom.trim() };
      } else {
        payload = { template: chosen };
      }
      const r = await api.post(`/platform/tenants/${tenantId}/nudge`, payload);
      if (r.data.provider === "mock") {
        toast.success(`Nudge queued for ${tenantName} (mock — Twilio KYC pending)`);
      } else if (r.data.status === "sent") {
        toast.success(`Nudge sent to ${tenantName} → ${r.data.phone}`);
      } else {
        toast.warning(`Nudge routed (${r.data.provider}/${r.data.status})`);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send nudge");
    } finally { setSending(false); }
  };

  const saveTemplate = async () => {
    if (!saveLabel.trim()) {
      toast.error("Give the template a name");
      return;
    }
    if (!custom.trim()) {
      toast.error("Type the message body first");
      return;
    }
    // Body must include {first} — auto-swap the tenant's firstName occurrence
    // so a user who typed "Hey Molle" gets it saved as "Hey {first}".
    let body = custom.trim();
    if (!body.includes("{first}")) {
      const re = new RegExp(`\\b${firstName.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}\\b`, "i");
      if (re.test(body)) {
        body = body.replace(re, "{first}");
      } else {
        toast.error("Include the recipient's first name (or the literal {first} placeholder) so it works for every tenant");
        return;
      }
    }
    setSaving(true);
    try {
      const key = saveLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
      await api.post("/platform/nudge/templates", { key, label: saveLabel.trim(), body });
      toast.success(`Saved template "${saveLabel.trim()}"`);
      setSaveOpen(false);
      setSaveLabel("");
      loadTemplates();
      setMode("template");
      setChosen(key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save template");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="nudge-picker">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <MessageCircle className="w-5 h-5" /> Nudge {tenantName}
          </DialogTitle>
          <DialogDescription>Pick a template or type an ad-hoc message.</DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="grid w-full grid-cols-2" data-testid="nudge-mode-tabs">
            <TabsTrigger value="template" data-testid="nudge-tab-template">Template</TabsTrigger>
            <TabsTrigger value="custom" data-testid="nudge-tab-custom">Custom message</TabsTrigger>
          </TabsList>

          <TabsContent value="template" className="mt-3">
            <RadioGroup value={chosen} onValueChange={setChosen} className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {templates.map((t) => (
                <div key={t.key} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${chosen === t.key ? "border-navy bg-navy/5" : "border-border hover:bg-muted/30"}`} onClick={() => setChosen(t.key)} data-testid={`nudge-template-${t.key}`}>
                  <RadioGroupItem value={t.key} id={`tpl-${t.key}`} className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor={`tpl-${t.key}`} className="font-mono-label text-navy cursor-pointer">
                      {TEMPLATE_LABELS[t.key] || t.label || t.key}
                      {!t.seeded && <span className="ml-2 text-[0.6rem] font-mono-label px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 border border-emerald-500/40">custom</span>}
                    </Label>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {t.preview.replace(/\{first\}/g, firstName)}
                    </div>
                  </div>
                </div>
              ))}
              {templates.length === 0 && (
                <div className="text-xs text-muted-foreground py-4 text-center">No templates yet — add one at /platform/nudge-templates or switch to Custom message.</div>
              )}
            </RadioGroup>
          </TabsContent>

          <TabsContent value="custom" className="mt-3 space-y-2">
            <Label className="font-mono-label text-navy text-[0.65rem] tracking-widest">Ad-hoc SMS</Label>
            <Textarea
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder={`Hey ${firstName} — quick heads-up on your dashboard…`}
              rows={5}
              maxLength={480}
              className="resize-none font-mono text-sm"
              data-testid="nudge-custom-textarea"
            />
            <div className="flex items-center justify-between text-[0.7rem] text-muted-foreground">
              <span>Sent as-is. No template interpolation. Include your name and a STOP-opt-out line for TCPA.</span>
              <span className="tabular-nums" data-testid="nudge-custom-charcount">{custom.length}/480</span>
            </div>

            {saveOpen ? (
              <div className="rounded-lg border border-navy/40 bg-navy/5 p-3 space-y-2" data-testid="nudge-save-form">
                <Label htmlFor="tpl-save-label" className="font-mono-label text-navy text-[0.65rem] tracking-widest">Template name</Label>
                <Input
                  id="tpl-save-label"
                  value={saveLabel}
                  onChange={(e) => setSaveLabel(e.target.value)}
                  placeholder="e.g. Q4 upsell nudge"
                  data-testid="nudge-save-label-input"
                  autoFocus
                />
                <div className="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setSaveOpen(false); setSaveLabel(""); }} data-testid="nudge-save-cancel-btn">Cancel</Button>
                  <Button size="sm" disabled={saving} onClick={saveTemplate} className="bg-navy hover:bg-navy/85" data-testid="nudge-save-confirm-btn">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (<><Save className="w-3.5 h-3.5 mr-1" /> Save template</>)}
                  </Button>
                </div>
                <div className="text-[0.65rem] text-muted-foreground">
                  We'll replace "{firstName}" with {"{first}"} so this template works for every tenant.
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSaveOpen(true)}
                disabled={!custom.trim()}
                data-testid="nudge-save-as-template-btn"
                className="w-full"
              >
                <Save className="w-3.5 h-3.5 mr-1.5" /> Save as template
              </Button>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="nudge-cancel-btn">Cancel</Button>
          <Button onClick={send} disabled={sending} className="bg-navy hover:bg-navy/85" data-testid="nudge-send-btn">
            {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-2" /> Fire nudge</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
